import { type ReactNode } from 'react';

import Sheet from './Sheet';

/**
 * The one question the app asks, in the one place it asks it.
 *
 * Four things in the app cannot be taken back — closing a rehearsal, cancelling
 * one, removing a gathering from the schedule, removing somebody from the choir
 * — and each of them used to ask in its own words and its own box. That box was
 * `.confirm`, and by the time it had spread to six screens with a different
 * layout on each, the way the app asked said nothing about the weight of what
 * it was asking. The rule of 2026-08-21: what is filled in or decided arrives
 * as a sheet, what is already done gets a bar with «Отменить» on it, and what
 * is merely shown unfolds in place. Three containers, and a fourth would undo
 * all three.
 *
 * The form is fixed, and it is fixed here so that no caller can invent its own:
 *
 * 1. The heading names the subject — «Убрать Петра Мельника из хора?»
 * 2. The paragraph names the price, with the figure in it. «Тем, кто не
 *    отмечен, запишется пропуск — сейчас это 6 человек» stops a hand; «вы
 *    уверены?» does not.
 * 3. One button, in the colour of danger, repeating the action as a verb.
 *
 * There is no «Не надо». Refusing is closing the sheet, and the sheet closes
 * three ways — the handle, the darkening, the back button. A fourth control
 * that does what three gestures already do would only crowd the one that
 * matters.
 *
 * The button that opens this stays outlined on the screen it stands on. Red is
 * for the one inside: until the question has been asked, nothing dangerous has
 * happened.
 */
export default function Ask({
  open,
  title,
  cost,
  verb,
  busy,
  failure,
  onConfirm,
  onClose,
}: {
  open: boolean;
  /** The subject, as a question. Also what a reader who cannot see it is told. */
  title: string;
  /** What it costs, with the figure spelled out. */
  cost: ReactNode;
  /** The action, as a verb: «Завершить», «Убрать». */
  verb: string;
  busy: boolean;
  /**
   * Why the server refused, if it did. It is drawn in here rather than on the
   * screen behind for the reason the add-a-gathering form's refusal is: the
   * sheet stays up until the request has gone through, so an answer left
   * behind the darkening is an answer nobody reads.
   */
  failure?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Sheet open={open} label={title} onClose={onClose}>
      <div className="ask">
        <p className="ask-title">{title}</p>
        <p className="ask-cost">{cost}</p>
        {failure !== undefined && failure !== null && <p className="failure">{failure}</p>}
        <button className="btn btn--danger" disabled={busy} onClick={onConfirm}>
          {verb}
        </button>
      </div>
    </Sheet>
  );
}
